import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { map, Observable, timeout, of, switchMap } from 'rxjs';
import { catchError, first } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  private auth = inject(AuthService);
  private readonly router = inject(Router);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.isLoggedIn();
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.isLoggedIn();
  }

  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.isLoggedIn();
  }

  private isLoggedIn(): Observable<boolean | UrlTree> {
    // Sprawdź czy użytkownik jest już zalogowany (szybka weryfikacja)
    if (this.auth.isLoggedIn) {
      return of(true);
    }

    // Sprawdź czy aplikacja jest w trakcie inicjalizacji
    if (this.auth.initializing) {
      // Poczekaj na zakończenie inicjalizacji
      return this.waitForInitialization();
    }

    // Sprawdź czy mamy token lub refresh token
    if (this.auth.token || this.auth.refreshToken) {
      // Poczekaj na inicjalizację użytkownika
      return this.waitForInitialization();
    }

    // Sprawdź czy użytkownik jest już na stronie logowania
    const currentUrl = this.router.url;
    if (currentUrl === '/login' || currentUrl.includes('login')) {
      console.debug('User already on login page, allowing access');
      return of(true);
    }

    // Brak danych autoryzacji - przekieruj do logowania
    console.debug('No auth data, redirecting to login from:', currentUrl);
    return of(this.router.createUrlTree(['/login']));
  }

  /**
   * Czeka na koniec startu sesji i decyduje po tym, czy jest użytkownik.
   *
   * Wcześniej czekało tu `getUser()`, które emituje tylko zalogowanego użytkownika —
   * bez sesji nie emitowało nic i strona stała pusta aż do 10-sekundowego limitu.
   */
  private waitForInitialization(): Observable<boolean | UrlTree> {
    return this.auth.whenInitialized().pipe(
      switchMap(() => this.auth.getCurrentUser().pipe(first())),
      timeout(10000),
      map((user) => {
        if (user) {
          return true;
        } else {
          return this.router.createUrlTree(['/login']);
        }
      }),
      catchError((err) => {
        console.error('AuthGuard error:', err);
        // Przy błędzie, przekieruj do logowania
        return of(this.router.createUrlTree(['/login']));
      })
    );
  }
}
